import { Link } from "react-router-dom";
import type { CommunityPost } from "@/mocks/community";

interface PostCardProps {
  post: CommunityPost;
  variant?: "default" | "wide" | "compact";
}

function formatCount(value: number) {
  if (value >= 10000) return `${(value / 10000).toFixed(1)}万`;
  return value.toLocaleString("zh-CN");
}

export default function PostCard({ post, variant = "default" }: PostCardProps) {
  if (variant === "compact") {
    return (
      <article className="group flex gap-3 border-b border-background-200 py-4 last:border-0">
        <div className="h-16 w-24 shrink-0 overflow-hidden rounded-lg bg-background-100">
          <img
            src={post.cover}
            alt={`${post.title}配图`}
            title={`${post.title} - 爆料社区`}
            className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0">
          <Link to={`/post/${post.id}`} className="block">
            <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
              {post.title}
            </h3>
          </Link>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-fire-line text-primary-500" aria-hidden="true" />
              热度 {post.heat}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-chat-3-line" aria-hidden="true" />
              {formatCount(post.commentCount)}
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === "wide") {
    return (
      <article className="group grid gap-4 overflow-hidden rounded-xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 sm:grid-cols-[220px_1fr] sm:p-4">
        <div className="h-40 overflow-hidden rounded-lg bg-background-100 sm:h-full sm:min-h-[150px]">
          <img
            src={post.cover}
            alt={`${post.title}配图`}
            title={`${post.title} - 爆料社区`}
            className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-col">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-900">
              {post.categoryName}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-primary-600">
              <i className="ri-fire-line" aria-hidden="true" />
              热度 {post.heat}
            </span>
          </div>
          <Link to={`/post/${post.id}`} className="mt-2 block">
            <h3 className="text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
              {post.title}
            </h3>
          </Link>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{post.excerpt}</p>
          <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-user-line" aria-hidden="true" />
              {post.author}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line" aria-hidden="true" />
              {post.publishedAt}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true" />
              {formatCount(post.views)}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-chat-3-line" aria-hidden="true" />
              {formatCount(post.commentCount)}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/post/${post.id}`} className="block h-44 overflow-hidden bg-background-100">
        <img
          src={post.cover}
          alt={`${post.title}配图`}
          title={`${post.title} - 爆料社区`}
          className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center justify-between gap-2">
          <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
            {post.categoryName}
          </span>
          <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600">
            <i className="ri-fire-line" aria-hidden="true" />
            {post.heat}
          </span>
        </div>
        <Link to={`/post/${post.id}`} className="mt-2 block">
          <h3 className="line-clamp-2 text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
            {post.title}
          </h3>
        </Link>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{post.excerpt}</p>
        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-4 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-chat-3-line" aria-hidden="true" />
            {formatCount(post.commentCount)} 回复
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-eye-line" aria-hidden="true" />
            {formatCount(post.views)} 围观
          </span>
        </div>
      </div>
    </article>
  );
}

export { formatCount };